"use client";

import { UserEvents, getMixpanelDistinctId, trackEvent } from "@/analytics";
import { useCallback, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { Comedian } from "@/data/comedians";
import { PunchupSpinner } from "@punchup/ui-common/punchup/primitives/feedback/PunchupSpinner";
import { ShowWithComedian } from "@/data/shows";
import { convertToAffiliate } from "@/utils/ticketmasterConverter";
import dayjs, { type Dayjs } from "@punchup/utils/dayjs";
import { useGate } from "statsig-react";

// The redirect fires as soon as the tracking event lands AND the third-party
// pixels have flushed. This is the upper bound on how long we hold the user if
// Statsig init, the Mixpanel callback, or a pixel script stalls (ad blocker,
// blocked network, uninitialized SDK) — navigation must never depend on
// analytics succeeding.
const REDIRECT_FALLBACK_MS = 3000;
// How often to re-check whether the third-party pixel scripts have executed.
const PIXEL_POLL_INTERVAL_MS = 100;
// A pixel script flushes its queued PageView synchronously when it executes;
// this grace lets those just-dispatched beacons leave before the page unloads.
const PIXEL_FLUSH_GRACE_MS = 300;

type PixelWindow = Window & {
  fbq?: { callMethod?: unknown };
  google_tag_manager?: unknown;
};

// The FB pixel and Google Ads gtag load `afterInteractive` from the root
// layout: an inline stub queues events immediately, and the real script sends
// them when it executes. Navigating away before that happens silently drops
// the queued PageViews, so the redirect waits (bounded by REDIRECT_FALLBACK_MS)
// for each configured pixel's real script:
// - fbevents.js defines fbq.callMethod (the inline stub leaves it unset, and
//   until the stub itself runs `fbq` is undefined — both count as pending)
// - gtag/js defines window.google_tag_manager (skipped entirely when no
//   NEXT_PUBLIC_GOOGLE_ADS_ID is configured)
// Waiting for gtag also lets the GA4 linker decorate `_gl` onto the outbound
// navigation. Hotjar is deliberately not gated on: it queues no attribution
// events — it records sessions, and there is nothing meaningful to record on
// a blank instant-redirect page.
//
// These signals are vendor internals (though fbq.callMethod is the stub's own
// dispatch contract, defined in our root layout snippet). If a vendor ever
// renames one, this check fails SAFE: pixelsDone never flips, and every
// visitor redirects at the REDIRECT_FALLBACK_MS cap instead of immediately —
// a bounded slowdown, never a stranded page or a lost Mixpanel event.
const thirdPartyPixelsFlushed = (): boolean => {
  const w = window as unknown as PixelWindow;
  const fbqLoaded = typeof w.fbq?.callMethod === "function";
  const gtagLoaded =
    !process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ||
    typeof w.google_tag_manager !== "undefined";
  return fbqLoaded && gtagLoaded;
};

const showAlreadyHappened = (datetime: Dayjs | null): boolean => {
  if (!datetime) {
    return false;
  }
  // Add an extra day since we do not support timezone, we don't accidentally not redirect to tickets for an event still valid
  if (datetime.add(1, "day").isBefore(dayjs())) {
    return true;
  } else {
    return false;
  }
};

export const TicketRedirect = ({
  show,
  comedian,
  referer,
}: {
  show: ShowWithComedian;
  comedian?: Comedian;
  referer?: string | null;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const source = searchParams.get("src");
  const { value: affiliateConverterEnabled, isLoading: gateLoading } = useGate(
    "ticket_redirect_affiliate_converter"
  );
  const trackingStartedRef = useRef(false);
  const redirectedRef = useRef(false);
  const ticketLinkRef = useRef<string | null>(null);

  const isPastEvent = showAlreadyHappened(
    show.datetime ? dayjs.utc(show.datetime) : null
  );

  const redirect = useCallback(
    (ticketLink: string) => {
      if (redirectedRef.current) {
        return;
      }
      redirectedRef.current = true;

      if (isPastEvent) {
        // For past events, redirect to comedian's tickets page if available,
        // otherwise to venue page if available, otherwise to ticket link
        if (comedian) {
          return router.push(`/${comedian.slug}/tickets`);
        }
        if (show.venue_pages[0]?.slug) {
          return router.push(`/v/${show.venue_pages[0].slug}`);
        }
      }
      // External ticket vendor: use a top-level document navigation so the
      // GA4 linker can decorate `_gl` onto the outbound URL.
      window.location.assign(ticketLink);
    },
    [comedian, isPastEvent, router, show.venue_pages]
  );

  useEffect(() => {
    // A dependency changed after tracking began (e.g. the Statsig value
    // flipped): the first run's timers were cleaned up, so re-arm only the
    // safety net — without it a lost callback would strand the visitor on a
    // blank page.
    if (trackingStartedRef.current) {
      const fallback = setTimeout(
        () => redirect(ticketLinkRef.current ?? show.ticket_link),
        REDIRECT_FALLBACK_MS
      );
      return () => clearTimeout(fallback);
    }

    // Hold tracking until the affiliate gate resolves so Ticketmaster links
    // get converted; the fallback below bounds the wait if Statsig stalls.
    if (gateLoading) {
      const fallback = setTimeout(
        () => redirect(show.ticket_link),
        REDIRECT_FALLBACK_MS
      );
      return () => clearTimeout(fallback);
    }

    trackingStartedRef.current = true;

    const ticketLink = affiliateConverterEnabled
      ? convertToAffiliate(show.ticket_link, {
          src: "splash_page",
          distinctId: getMixpanelDistinctId(),
        })
      : show.ticket_link;
    ticketLinkRef.current = ticketLink;

    // Dual gate: navigate only once the Mixpanel event has landed AND the
    // third-party pixels have flushed their queued PageViews.
    let mixpanelLanded = false;
    let pixelsDone = thirdPartyPixelsFlushed();
    const tryRedirect = () => {
      if (mixpanelLanded && pixelsDone) {
        redirect(ticketLink);
      }
    };

    trackEvent(
      isPastEvent
        ? UserEvents.TICKET_REDIRECT_PAST_EVENT
        : UserEvents.TICKET_REDIRECT,
      {
        ComedianId: show.comedian_id,
        ComedianName: comedian?.display_name || show.title || show.venue,
        ShowVenue: show.venue,
        ShowDate: show.datetime,
        ShowId: show.id,
        Referer: referer || "",
        ...(source ? { InternalReferrer: source } : {}),
      },
      { send_immediately: true },
      () => {
        mixpanelLanded = true;
        tryRedirect();
      }
    );

    let poll: ReturnType<typeof setInterval> | undefined;
    let graceTimer: ReturnType<typeof setTimeout> | undefined;
    if (!pixelsDone) {
      poll = setInterval(() => {
        if (!thirdPartyPixelsFlushed()) {
          return;
        }
        clearInterval(poll);
        // The script just executed and flushed its queue; give the dispatched
        // beacons a beat to leave before unloading the page.
        graceTimer = setTimeout(() => {
          pixelsDone = true;
          tryRedirect();
        }, PIXEL_FLUSH_GRACE_MS);
      }, PIXEL_POLL_INTERVAL_MS);
    }

    // Safety net: never strand the user if the tracking callback never fires
    // or an ad blocker keeps the pixels pending forever
    const fallback = setTimeout(
      () => redirect(ticketLink),
      REDIRECT_FALLBACK_MS
    );
    return () => {
      if (poll) {
        clearInterval(poll);
      }
      if (graceTimer) {
        clearTimeout(graceTimer);
      }
      clearTimeout(fallback);
    };
  }, [
    affiliateConverterEnabled,
    comedian,
    gateLoading,
    isPastEvent,
    redirect,
    referer,
    show,
    source,
  ]);

  // Minimal interstitial — a centered spinner above one line of text on an
  // otherwise bare white page (the app chrome is suppressed in
  // ScrollablePageLayout). A fully blank page made visitors on slow
  // connections think the page was broken and click away before the
  // tracking-gated redirect fired. Past events swap the message for an
  // explanation, since the visitor lands somewhere that doesn't sell the
  // tickets they asked for.
  const destinationName =
    show.title || comedian?.display_name || show.venue || "your tickets";
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-4 text-center text-black">
      <PunchupSpinner size={32} />
      <p className="text-md">
        {isPastEvent ? (
          <>
            <span className="font-emoji px-1">😭</span> Oops, this event has
            already happened
            {comedian
              ? ", redirecting you to the rest of the tour"
              : ", redirecting you to more events"}
          </>
        ) : (
          <>Taking you to {destinationName}...</>
        )}
      </p>
    </div>
  );
};
